import React from 'react'
import { Route, Redirect } from 'react-router-dom'
import { ConnectedRouter } from 'connected-react-router'
import { CSSTransition, SwitchTransition } from 'react-transition-group'
import Switch from 'react-router-transition-switch'
import { connect } from 'react-redux'
import Layout from 'layouts'
import { ProtectedRoute } from 'components/common'
import NotFoundPage from 'pages/system/404'
import routes from './routes'

// Slugs that belong exclusively to the customer portal.
// A user holding ONLY these slugs has no business in the back-office.
// NOTE: customer_view is intentionally excluded — it is a back-office permission
//       for viewing customer contact info, not a portal-access slug.
const CUSTOMER_ONLY_SLUGS = new Set([
  'customer_dashboard',
  'customer_wo_request',
  'customer_proposal_request',
  'customer_proposal_approve',
  'proposal_budget_approval',
])

// Permission-aware root redirect:
//   1. Has customer_dashboard → customer portal
//   2. Has at least one back-office permission → back-office
//   3. Neither → no-access page (account misconfigured)
const RootRedirect = connect(({ user }) => ({ user }))(({ user }) => {
  const permissions = user.permissions || []
  const slug = p => (typeof p === 'string' ? p : p?.slug)

  const isCustomerPortalUser = permissions.some(p => slug(p) === 'customer_dashboard')

  if (isCustomerPortalUser) {
    return <Redirect to="/customer/dashboard" />
  }

  const hasBackofficeAccess = permissions.some(p => {
    const s = slug(p)
    return s && !CUSTOMER_ONLY_SLUGS.has(s)
  })
  if (hasBackofficeAccess) {
    return <Redirect to="/contracts" />
  }

  return <Redirect to="/system/no-access" />
})

@connect(({ settings }) => ({ settings }))
class Router extends React.Component {
  render() {
    const {
      history,
      settings: { routerAnimation },
    } = this.props
    return (
      <ConnectedRouter history={history}>
        <Layout>
          <Switch
            render={props => {
              const {
                children,
                location: { pathname },
              } = props
              return (
                <SwitchTransition>
                  <CSSTransition
                    key={pathname}
                    classNames={routerAnimation}
                    timeout={routerAnimation === 'none' ? 0 : 300}
                  >
                    {children}
                  </CSSTransition>
                </SwitchTransition>
              )
            }}
          >
            <Route exact path="/" render={() => <RootRedirect />} />

            {routes.map(({ path, Component, exact, requiredPermissions, anyPermissions }) => {
              // If route has any access control, wrap with ProtectedRoute
              const needsGuard =
                (requiredPermissions && requiredPermissions.length > 0) ||
                (anyPermissions && anyPermissions.length > 0)

              if (needsGuard) {
                return (
                  <Route path={path} key={path} exact={exact}>
                    <ProtectedRoute
                      path={path}
                      Component={Component}
                      requiredPermissions={requiredPermissions}
                      anyPermissions={anyPermissions}
                      exact={exact}
                    />
                  </Route>
                )
              }

              // Otherwise render normally (public routes like login, 404, etc.)
              return (
                <Route path={path} key={path} exact={exact}>
                  <Component />
                </Route>
              )
            })}
            <Route component={NotFoundPage} />
          </Switch>
        </Layout>
      </ConnectedRouter>
    )
  }
}

export default Router
